import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

import useConditionalAction from './useConditionalAction'
import useInsuranceSummary from './useInsuranceSummary'

/**
 * Hook for handling Gilead patient warning messages
 * Shows warning for patients who are active, uninsured, and have Gilead benefits
 *
 * @param {Object} patient - Patient object
 * @param {string} section - Section identifier, for example: encounter, appointment, rx, lab
 * @returns {Function} wrappedAction - Function that wraps actions with Gilead warning
 */
export default function useGileadWarning(patient, section) {
  const { data: insuranceSummary } = useInsuranceSummary(patient?.id)

  const shouldShowConfirmation = useCallback(() => {
    if (!patient) return false

    // Check if patient has assistance program in their last active insurance
    return Boolean(insuranceSummary?.lastActiveInsurance?.assistanceProgram)
  }, [patient, insuranceSummary])

  const description = useMemo(() => {
    if (!patient?.gileadExpDate || dayjs().isAfter(dayjs(patient.gileadExpDate), 'day')) {
      return 'Patient is waitlisted. Please reach out to your CSR to confirm benefits first.'
    }

    const formattedDate = dayjs(patient.gileadExpDate).format('L')
    return `Patient is waitlisted. Gilead benefits are valid through ${formattedDate}`
  }, [patient?.gileadExpDate])

  const entityKey = useMemo(() => {
    if (!patient?.id || !section) return null
    return `gilead-warning-${patient.id}-${section}`
  }, [patient?.id, section])

  return useConditionalAction({
    shouldShowConfirmation,
    component: ConfirmationV2,
    getConfirmationProps: () => ({
      variant: 'warning',
      title: 'Patient Notice',
      description,
      rejectLabel: 'Cancel',
      confirmLabel: 'Continue',
    }),
    entityKey,
  })
}
